import axios from 'axios';

const DB_NAME = 'pama-offline';
const STORE = 'queue';
const CHANGED = 'offline-queue-changed';

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const result = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(result?.result ?? result);
    t.onerror = () => reject(t.error);
  });
};

const notify = () => window.dispatchEvent(new Event(CHANGED));
const currentUserId = () => JSON.parse(localStorage.getItem('user') || '{}').id;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const newId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const listQueue = async () => {
  const all = await tx('readonly', (s) => s.getAll());
  return all.filter((i) => i.userId === currentUserId()).sort((a, b) => a.createdAt - b.createdAt);
};

const putItem = async (item) => {
  await tx('readwrite', (s) => s.put(item));
  notify();
};

export const removeItem = async (id) => {
  await tx('readwrite', (s) => s.delete(id));
  notify();
};

const send = ({ method, url, json, file, fileName }) => {
  if (file) {
    const fd = new FormData();
    fd.append('file', file, fileName);
    return axios({ method, url, data: fd, headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
  }
  return axios({ method, url, data: json, headers: authHeaders() });
};

const isNetworkError = (err) => !err.response;

// Invia subito; senza rete mette in coda e restituisce { queued: true }
export const offlineSend = async (request) => {
  if (navigator.onLine) {
    try {
      return await send(request);
    } catch (err) {
      if (!isNetworkError(err)) throw err;
    }
  }
  const baseUrl = (u) => u.split('?')[0];
  if (request.method === 'put') {
    const same = (await listQueue()).find(
      (i) => i.method === 'put' && i.status === 'pending' && baseUrl(i.url) === baseUrl(request.url)
    );
    if (same) {
      await putItem({ ...same, json: request.json, label: request.label });
      return { queued: true };
    }
  }
  await putItem({
    ...request,
    url: request.offlineUrl || request.url,
    fileName: request.fileName || request.file?.name,
    id: newId(),
    userId: currentUserId(),
    createdAt: Date.now(),
    status: 'pending',
  });
  return { queued: true };
};

let syncing = false;

export const processQueue = async () => {
  if (syncing || !navigator.onLine || !localStorage.getItem('token')) return { sent: 0 };
  syncing = true;
  let sent = 0;
  try {
    for (const item of await listQueue()) {
      if (item.status !== 'pending') continue;
      try {
        await send(item);
        await removeItem(item.id);
        sent += 1;
      } catch (err) {
        if (isNetworkError(err)) break;
        const status = err.response.status;
        const detail = err.response.data?.detail;
        await putItem({
          ...item,
          status: status === 409 ? 'conflict' : 'error',
          errorMessage: typeof detail === 'string' ? detail : detail?.message || `Errore ${status}`,
          serverUpdatedBy: detail?.updated_by,
        });
      }
    }
  } finally {
    syncing = false;
  }
  if (sent) window.dispatchEvent(new CustomEvent('offline-queue-synced', { detail: { sent } }));
  return { sent };
};

// "mine": rimanda senza controllo di versione; "retry": riprova così com'è
export const retryItem = async (item, mode) => {
  const url = mode === 'mine' ? item.url.replace(/[?&]if_unmodified_since=[^&]*/, '') : item.url;
  await putItem({ ...item, url, status: 'pending', errorMessage: undefined });
  return processQueue();
};

export const OFFLINE_QUEUE_CHANGED = CHANGED;
